import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { RegistrationService } from 'src/app/services/registration-service';
import { SessionService } from 'src/app/services/session-service';
import { QuickSignupService } from 'src/app/services/quick-signup-service';
import { environment } from 'src/environments/environment';
import { showUnfinished } from 'src/app/site-config';

// Member "Add a photo" supply path (Plate Image Researcher, Increment 2).
// Login-gated: a signed-in member submits a credited photo of the plate on a
// car. It is NOT shown publicly immediately; it lands pending and an editor
// approves it (two-gate model). The contributor, however, sees their own photo
// straight away, badged "Pending review" (see photoAdded output), so the act of
// contributing feels acknowledged rather than swallowed.
//
// Mobile-first: the photo field offers a direct "Take photo" camera capture, a
// live preview, and the "seen" year is read from the photo's own EXIF capture
// date so the member rarely has to set it. The plate itself is never typed — it
// is the page's registration. The car defaults to the current assignment.
export interface KnownCar {
  label: string; // "Land Rover Range Rover · 2020–2023"
  make: string;
  model: string;
  colour: string;
  year?: string; // the car's timeline year, to pin the photo to the right row
}

// Emitted the moment a submit succeeds, so the host can show the contributor
// their own pending photo in the timeline without waiting for a reload.
export interface PendingPhotoAdded {
  image: string | SafeUrl; // trusted local object URL for instant preview
  make: string;
  model: string;
  colour: string;
  seen: string;
  credit: string; // "a PlateX user" or the member's chosen handle
  matchYear?: string; // chosen known-car's timeline year, for correct placement
}

@Component({
  selector: 'pla-pdv2-photo-add',
  templateUrl: './photo-add.component.html',
  styleUrls: ['./photo-add.component.scss'],
})
export class PhotoAddComponent implements OnChanges {
  @Input() registration = '';
  // Spaced mark for copy ("D 88"); `registration` stays the compact key the
  // upload is filed under.
  @Input() displayRegistration = '';
  @Input() knownCars: KnownCar[] = [];
  @Output() photoAdded = new EventEmitter<PendingPhotoAdded>();

  public open = false;
  public submitting = false;
  public submitted = false;
  public error: string | null = null;

  // form model
  public imageUrl = '';
  public file: File | null = null;
  public previewUrl: string | null = null; // raw object URL (kept for revoke)
  public previewSafe: SafeUrl | null = null; // sanitizer-trusted, for the <img>
  public carChoice = 'other'; // 'known:<i>' | 'other'
  public make = '';
  public model = '';
  public colour = '';
  public year: number = new Date().getFullYear();
  public source = '';
  // Default to the privacy-preserving credit the founder chose for the bulk set.
  public anonymous = true;

  public readonly years: number[] = Array.from(
    { length: 22 },
    (_, i) => new Date().getFullYear() - i
  );

  constructor(
    private sessionService: SessionService,
    private registrationService: RegistrationService,
    private sanitizer: DomSanitizer,
    private el: ElementRef<HTMLElement>,
    private quickSignup: QuickSignupService
  ) {}

  // Camera / upload on the compact prompt: keep the chosen photo, open the
  // form with its preview, and ask a signed-out visitor to sign in (modal, so
  // the page and the photo stay put).
  public onPromptFile(ev: Event): void {
    this.onFile(ev);
    if (!this.file) return;
    this.open = true;
    this.requireSignIn(() => {});
  }

  private requireSignIn(then: () => void): void {
    if (this.isLoggedIn) {
      then();
      return;
    }
    this.quickSignup.quickSignup(
      'Sign in to add your photo',
      undefined,
      (ok: boolean) => {
        if (ok) then();
      },
      () => {}
    );
  }

  // Opened from a timeline vehicle card: preselect that car (matched on its
  // timeline year) and bring the form, or the sign-in prompt, into view.
  public openFor(year: string): void {
    const i = (this.knownCars || []).findIndex((c) => c.year === year);
    if (i >= 0) this.carChoice = 'known:' + i;
    if (!this.submitted) this.open = true;
    setTimeout(() =>
      this.el.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
    );
  }

  public ngOnChanges(changes: SimpleChanges): void {
    // When we learn the plate's known cars, default the picker to the current
    // vehicle (first entry) so the common case is zero taps.
    if (changes['knownCars'] && this.knownCars && this.knownCars.length) {
      if (this.carChoice === 'other') this.carChoice = 'known:0';
    }
  }

  public get isLoggedIn(): boolean {
    return this.sessionService.isLoggedIn();
  }

  public get addingNewCar(): boolean {
    return this.carChoice === 'other';
  }

  // Stable identity for the car <option>s so the native mobile <select> can
  // commit a choice without the list re-rendering under it.
  public trackCar(i: number, c: KnownCar): string {
    return (c && c.label) || String(i);
  }

  public onFile(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    this.file = input.files && input.files.length ? input.files[0] : null;
    this.imageUrl = '';
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
      this.previewSafe = null;
    }
    if (this.file) {
      this.previewUrl = URL.createObjectURL(this.file);
      // Angular's URL sanitizer rejects blob: URLs in [src] (renders
      // "unsafe:blob:…" and the image never loads). We created it ourselves, so
      // mark it trusted.
      this.previewSafe = this.sanitizer.bypassSecurityTrustUrl(this.previewUrl);
      this.readExifYear(this.file);
    }
  }

  public clearPhoto(): void {
    this.file = null;
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
      this.previewSafe = null;
    }
  }

  // Read the JPEG EXIF DateTimeOriginal (tag 0x9003) / DateTime (0x0132) and set
  // the "seen" year from it. Pure client-side, defensively guarded; falls back
  // to the file's last-modified year, then leaves the current year. We only need
  // the year, so a full EXIF library would be overkill.
  private readExifYear(file: File): void {
    const fallback = () => {
      const y = file.lastModified
        ? new Date(file.lastModified).getFullYear()
        : 0;
      if (y >= 1990 && y <= new Date().getFullYear()) this.year = y;
    };
    if (!/jpe?g/i.test(file.type)) {
      fallback();
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const y = this.exifYearFromJpeg(reader.result as ArrayBuffer);
        if (y >= 1990 && y <= new Date().getFullYear()) this.year = y;
        else fallback();
      } catch {
        fallback();
      }
    };
    reader.onerror = () => fallback();
    // The EXIF block sits in the first APP1 segment near the file head.
    reader.readAsArrayBuffer(file.slice(0, 131072));
  }

  private exifYearFromJpeg(buf: ArrayBuffer): number {
    const view = new DataView(buf);
    if (view.getUint16(0) !== 0xffd8) return 0; // not a JPEG
    let offset = 2;
    const len = view.byteLength;
    while (offset + 4 < len) {
      if (view.getUint16(offset) !== 0xffe1) {
        // skip this marker segment
        const size = view.getUint16(offset + 2);
        offset += 2 + size;
        continue;
      }
      // APP1 — expect "Exif\0\0"
      const exifStart = offset + 4;
      if (view.getUint32(exifStart) !== 0x45786966) return 0; // "Exif"
      const tiff = exifStart + 6;
      const little = view.getUint16(tiff) === 0x4949;
      const g16 = (o: number) => view.getUint16(o, little);
      const g32 = (o: number) => view.getUint32(o, little);
      const ifd0 = tiff + g32(tiff + 4);
      const dateStr = (dirStart: number): string => {
        const count = g16(dirStart);
        for (let i = 0; i < count; i++) {
          const entry = dirStart + 2 + i * 12;
          const tag = g16(entry);
          if (tag === 0x9003 || tag === 0x0132) {
            const valOff = tiff + g32(entry + 8);
            let s = '';
            for (let k = 0; k < 10; k++)
              s += String.fromCharCode(view.getUint8(valOff + k));
            return s; // "YYYY:MM:DD"
          }
          if (tag === 0x8769) {
            // ExifIFD pointer — recurse for DateTimeOriginal
            const sub = tiff + g32(entry + 8);
            const r = dateStr(sub);
            if (r) return r;
          }
        }
        return '';
      };
      const s = dateStr(ifd0);
      const y = parseInt((s || '').slice(0, 4), 10);
      return isNaN(y) ? 0 : y;
    }
    return 0;
  }

  public submit(): void {
    this.error = null;
    if (!this.isLoggedIn) {
      this.requireSignIn(() => this.submit());
      return;
    }
    if (!this.file && !this.imageUrl.trim()) {
      this.error = 'Add a photo, by camera, upload or link.';
      return;
    }
    let make = this.make;
    let model = this.model;
    let colour = this.colour;
    let matchYear = '';
    if (!this.addingNewCar) {
      const i = parseInt(this.carChoice.split(':')[1], 10);
      const c = this.knownCars[i];
      if (c) {
        make = c.make;
        model = c.model;
        colour = c.colour;
        matchYear = c.year || '';
      }
    }
    if (!make.trim()) {
      this.error = 'Tell us the car make.';
      return;
    }

    const credit = this.anonymous
      ? 'a PlateX user'
      : this.source.trim() || 'a PlateX user';

    const onOk = () => {
      this.submitting = false;
      this.submitted = true;
      // Show the contributor their own pending photo straight away.
      this.photoAdded.emit({
        image: this.previewSafe || this.imageUrl.trim(),
        make,
        model,
        colour,
        seen: String(this.year),
        credit,
        matchYear,
      });
    };
    const onErr = () => {
      this.submitting = false;
      this.error = 'Could not submit just now. Please try again in a moment.';
    };

    this.submitting = true;

    // DEV2: there is no member-photo API deployed, so stage the upload to the
    // S3 inbox (editor promotes it later). Needs the actual file bytes.
    if (this.isDev2 && this.file && environment.plateInboxUrl) {
      this.uploadToInbox(make, model, colour).then(onOk).catch(onErr);
      return;
    }

    const fd = new FormData();
    fd.append('registration', this.registration);
    if (this.file) fd.append('file', this.file);
    if (this.imageUrl.trim()) fd.append('imageUrl', this.imageUrl.trim());
    fd.append('make', make);
    fd.append('model', model);
    fd.append('colour', colour);
    fd.append('year', String(this.year));
    fd.append('source', this.anonymous ? '' : this.source.trim());
    fd.append('anonymous', String(this.anonymous));

    this.registrationService.submitPlatePhoto(this.registration, fd, onOk, onErr);
  }

  // Member photo upload is dev-only (withheld on staging and production).
  public get isDev2(): boolean {
    return showUnfinished;
  }

  // Two-step staging upload: ask the inbox Lambda for a presigned POST (it also
  // writes the trusted metadata sidecar), then upload the image bytes straight
  // to the private S3 inbox. Uses fetch, not HttpClient, so the app's auth/url
  // interceptors don't attach a Bearer token or rewrite the AWS URLs.
  private async uploadToInbox(
    make: string,
    model: string,
    colour: string
  ): Promise<void> {
    const file = this.file as File;
    const presignRes = await fetch(environment.plateInboxUrl, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'x-upload-token': environment.plateInboxToken,
      },
      body: JSON.stringify({
        registration: this.registration,
        make,
        model,
        colour,
        year: this.year,
        source: this.anonymous ? '' : this.source.trim(),
        anonymous: this.anonymous,
        contentType: file.type || 'image/jpeg',
      }),
    });
    if (!presignRes.ok) throw new Error('presign failed');
    const data = await presignRes.json();
    const upload = data.upload;

    const fd = new FormData();
    Object.keys(upload.fields).forEach((k) => fd.append(k, upload.fields[k]));
    fd.append('file', file);
    const s3Res = await fetch(upload.url, { method: 'POST', body: fd });
    if (s3Res.status !== 204 && !s3Res.ok) throw new Error('s3 upload failed');
  }
}
